import { inject } from '@angular/core';
import { type WritableDraft } from 'immer';
import { first, type Observable, switchMap } from 'rxjs';

import { type CanvasElementFrom, type CanvasElementType } from '@blaze-runtime/engine-domain';
import { type CanvasElementInputBinding } from '@blaze-shared/elements-domain';

import { type EngineState } from '../../+state/engine';
import { resolveActionIcons } from '../common/resolve-action-icons';
import { type BulkAction, type RowAction } from '../models/actions';
import { type ElementContext, type WorkflowContext } from '../models/ai.model';
import { AiWorkflowGenerationService } from '../workflow';

import { type ElementHandler } from './element-handler';
import { IconResolverService } from './icon-resolver.service';

export abstract class AbstractAiElementHandler<T extends CanvasElementType>
  implements ElementHandler<T>
{
  protected readonly iconResolverService = inject(IconResolverService);
  protected readonly aiWorkflowGenerationService = inject(AiWorkflowGenerationService);

  abstract readonly type: T;
  abstract process(context: ElementContext<T>, draft: WritableDraft<EngineState>): void;

  protected resolveWorkflowContext(context: ElementContext<T>): WorkflowContext | undefined {
    return context.componentWorkflows[context.element.id];
  }

  protected resolveCustomTemplateCode(context: ElementContext<T>): string | undefined {
    return context.componentTemplates[context.element.id];
  }

  protected resolveElementIcon(element: { iconName?: string }): void {
    if (element.iconName) {
      element.iconName = this.iconResolverService.resolveFullIconName(element.iconName);
    }
  }

  protected resolveIcons(
    element: { iconName?: string },
    ...actionLists: (RowAction[] | BulkAction[] | undefined)[]
  ): void {
    this.resolveElementIcon(element);
    resolveActionIcons(actionLists, this.iconResolverService);
  }

  protected finalizeSourceWorkflow(
    element: CanvasElementFrom<T>,
    workflowContext: WorkflowContext,
    bindingKey: CanvasElementInputBinding = 'value',
    visibleFields?: string[],
  ): Observable<void> {
    const { workflow, inputVariables, resolvedLayoutInputs } = workflowContext;

    return this.aiWorkflowGenerationService.getWorkflowRelatedDetails(element.id).pipe(
      first(),
      switchMap(({ relatedDatasetMap, relatedDetails }) =>
        this.aiWorkflowGenerationService.finalizeAiWorkflowCreation(
          element.id,
          workflow,
          inputVariables,
          relatedDatasetMap,
          relatedDetails,
          bindingKey,
          undefined,
          visibleFields,
          resolvedLayoutInputs,
        ),
      ),
    );
  }
}
